import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { Shield, Lock, LogOut } from 'lucide-react';

const AdminPinPage = () => {
  const [pin, setPin] = useState('');
  const [pinError, setPinError] = useState(false);
  const { verifyAdminPin, logout, user } = useAuth();
  const navigate = useNavigate();

  const handlePinSubmit = () => {
    if (verifyAdminPin(pin)) {
      toast.success('Acceso de administrador verificado');
      navigate('/admin');
    } else {
      setPinError(true);
      setPin('');
      toast.error('PIN incorrecto');
    }
  };

  const handlePinKeyDown = (e) => {
    if (e.key === 'Enter' && pin.length === 4) {
      handlePinSubmit();
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-primary mb-2">GuardiansFit</h1>
          <p className="text-muted-foreground">Panel de Administración</p>
        </div>

        <Card className="bg-card border-border">
          <CardContent className="p-8">
            <div className="text-center mb-6">
              <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="h-10 w-10 text-primary" />
              </div>
              <h2 className="text-xl font-bold text-foreground">Verificación de Seguridad</h2>
              <p className="text-sm text-muted-foreground mt-2">
                Hola <span className="text-primary font-medium">{user?.name || 'Admin'}</span>, introduce tu PIN de administrador
              </p>
            </div>

            <div className="space-y-6">
              <div className="space-y-2">
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                  <Input
                    type="password"
                    maxLength={4}
                    value={pin}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '');
                      setPin(value);
                      setPinError(false);
                    }}
                    onKeyDown={handlePinKeyDown}
                    placeholder="• • • •"
                    className={`text-center text-3xl tracking-[0.8em] h-16 pl-12 bg-zinc-900/50 border-zinc-700 ${pinError ? 'border-red-500 animate-shake' : ''}`}
                    autoFocus
                    data-testid="admin-pin-input"
                  />
                </div>
                {pinError && (
                  <p className="text-red-500 text-sm text-center">PIN incorrecto. Inténtalo de nuevo.</p>
                )}
              </div>

              <Button 
                onClick={handlePinSubmit}
                className="w-full h-12 bg-primary text-primary-foreground font-bold"
                disabled={pin.length !== 4}
                data-testid="admin-pin-submit"
              >
                <Shield className="h-5 w-5 mr-2" />
                Verificar PIN
              </Button>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t border-zinc-700" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-card px-2 text-muted-foreground">o</span>
                </div>
              </div>

              <Button 
                variant="outline"
                onClick={handleLogout}
                className="w-full border-zinc-700 text-muted-foreground hover:text-foreground"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Cerrar sesión
              </Button>
            </div>

            <p className="text-xs text-center text-muted-foreground mt-6">
              Si olvidaste el PIN, contacta con soporte técnico
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminPinPage;
